// src/utils/useDialog.js
import { h, render, ref, onMounted, onUnmounted } from "vue";
import AppAlert from "@/components/AppAlert.vue";
import AppToast from "@/components/AppToast.vue";
import AppLoading from "@/components/AppLoading.vue";

export const useDialog = () => {
  let isShowing = false;

  const alert = (message, options = {}) => {
    return createDialog("alert", message, options);
  };

  const confirm = (message, options = {}) => {
    return createDialog("confirm", message, options);
  };

  const createDialog = (type, message, options) => {
    // 💡 이미 떠 있다면 그냥 종료 (무한 중첩 방지)
    if (isShowing) return Promise.resolve(null);

    isShowing = true; // 팝업 시작!

    return new Promise((resolve) => {
      const container = document.createElement("div");
      document.body.appendChild(container);

      const close = (result) => {
        isShowing = false; // 💡 닫힐 때 플래그 해제
        render(null, container);
        container.remove();
        resolve(result);
      };

      const vnode = h(AppAlert, {
        isOpen: true, // 즉시 표시
        type,
        message,
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        resolve: close, // 버튼 클릭 시 close 호출
      });

      render(vnode, container);
    });
  };

  return { alert, confirm };
};

export const useToast = () => {
  const show = (message, duration = 3000) => {
    // 1. 토스트 바구니(.toast-field)가 없으면 생성 (접근성 유지)
    let container = document.querySelector(".toast-field");
    if (!container) {
      container = document.createElement("div");
      container.className = "toast-field";
      container.setAttribute("aria-live", "polite");
      document.body.appendChild(container);
    }

    // 2. 각 토스트가 그려질 개별 mount point 생성
    const toastWrapper = document.createElement("div");
    container.appendChild(toastWrapper);

    // 3. 토스트 렌더링
    const vnode = h(AppToast, {
      message,
      duration,
      // Transition이 완전히 끝나면 실행될 콜백
      onDestroy: () => {
        render(null, toastWrapper); // Vue 인스턴스 해제
        toastWrapper.remove(); // DOM 제거

        // 바구니가 비었으면 바구니도 삭제
        if (container.childNodes.length === 0) {
          container.remove();
        }
      },
    });

    render(vnode, toastWrapper);
  };

  return { show };
};

let container = null; // 파일 전체에서 공유되는 단 하나의 변수, 없으면 로딩 중복
let vnode = null;

export const useLoading = () => {
  const show = () => {
    // 이미 로딩이 떠 있다면 중복 생성 방지
    if (container) return;

    container = document.createElement("div");
    container.className = "loading-wrapper";
    document.body.appendChild(container);

    vnode = h(AppLoading);
    render(vnode, container);
  };

  const hide = () => {
    if (!vnode || !container) return;

    // 1. 컴포넌트 내부의 isVisible을 false로 바꿈 (Fade-out 시작)
    vnode.component.exposed.hide();

    // 2. 기존 코드의 200ms 대기 후 DOM 삭제 로직 수행
    setTimeout(() => {
      render(null, container);
      container.remove();
      container = null;
      vnode = null;
    }, 200);
  };

  return { show, hide };
};

/**
 * 날짜 포맷팅
 * @param {String} dateString - 날짜 문자열 8자리 yyyymmdd (default : new Date)
 * @param {String} formatType - 날짜 연결형태 (ex. dash = yyyy-mm-dd)
 * @description - 오늘 현재날짜 기준으로 표출을 원할때는 dateString값을 null로 지정해주면 됩니다.
 */
export const dateRelc = (dateString, formatType) => {
  let dateY = "",
    dateM = "",
    dateD = "";

  if (!dateString) {
    dateString = new Date();

    dateY = dateString.getFullYear();
    dateM = dateString.getMonth() + 1;
    dateD = dateString.getDate();

    
  } else if (typeof dateString === "object") {
    dateY = dateString?.year;
    dateM = dateString?.month;
    dateD = dateString?.day;
  } else {
    dateY = dateString?.substring(0, 4);
    dateM = dateString?.substring(4, 6);
    dateD = dateString?.substring(6, 8);
  }

  dateM = String(dateM)?.padStart(2, "0");
  dateD = String(dateD)?.padStart(2, "0");

  if (formatType === "number") {
    return Number(`${dateY}${dateM}${dateD}`);
  } else {
    switch (formatType) {
      case "dash":
        return `${dateY}-${dateM}-${dateD}`;
      case "string":
        return `${dateY}년 ${dateM}월 ${dateD}일`;
      case "dot":
        return `${dateY}.${dateM}.${dateD}`;
      default:
        return `${dateY}${dateM}${dateD}`;
    }
  }
};

/**
 * 날짜 계산
 * @param {String} dateString - 날짜 문자열 8자리 필수!! yyyymmdd (default : new Date)
 * @param {String} unit - 년, 월 일 중 계산할 타입 (Y:년, M:월, D:일)
 * @param {String} operator - 더하거나 빼거나 선택할 타입 (p:더하기, m:빼기, equals: 비교)
 * @param {Number} amount - 계산할 값
 * @return {String} 계산된 8자리 문자열
 */
export const dateMath = (dateString, unit, operator, amount, checkDate) => {
    let dateY = "",
        dateM = "",
        dateD = "";

    if (!dateString) {
        dateString = new Date();

        dateY = Number(dateString.getFullYear());
        dateM = Number(dateString.getMonth());
        dateD = Number(dateString.getDate());
    } else {
        dateY = Number(dateString.substring(0, 4));
        dateM = Number(dateString.substring(4, 6)) - 1;
        dateD = Number(dateString.substring(6, 8));
    }

    if (operator === "equals") {
        const firstDate = Number(String(dateString)?.replace(/\D/g,""));
        const secDate = Number(String(checkDate)?.replace(/\D/g,""));

        return firstDate === secDate 
            ? 'same' : firstDate > secDate
                ? 'first' : 'second'

    }

    const targetDate = new Date(dateY, dateM, dateD);

    const calcValue = operator === "m" ? -Number(amount) : Number(amount);

    if (unit === "d") {
        targetDate.setDate(targetDate.getDate() + calcValue);
    } else {
        // 월 이동시 날짜가 튀는걸 방지 하기 위해 임시로 1로 세팅
        targetDate.setDate(1);

        if (unit === "y") {
        targetDate.setFullYear(dateY + calcValue);
        } else if (unit === "m") {
        targetDate.setMonth(dateM + calcValue);
        }
    }

    // 해당월의 마지막일 구하기
    const maxDay = new Date(
        targetDate.getFullYear(),
        targetDate.getMonth() + 1,
        0,
    ).getDate();

    targetDate.setDate(Math.min(dateD, maxDay));

    //문자열 조립
    const setY = targetDate.getFullYear();
    const setM = String(targetDate.getMonth() + 1).padStart(2, "0");
    const setD = String(targetDate.getDate()).padStart(2, "0");

    return `${setY}${setM}${setD}`;
};

/**
 * 휠피커 UI
 * @param {HTMLElement} wrapperEl - 피커들을 감싸는 최상위 DOM 요소
 * @param {Array} keys - 매핑할 데이터 키배열 (ex. ['year', 'month', 'day'])
 * @param {Obejct} resData - 초기 스크롤 위치를 잡기위한 데이터 객체
 * @param {Number} delay - 렌더링 대기 시간 기본 (200ms)
 * @param {Funtion} onChange - 체인지 이벤트
 * @return {Function} - 옵저버 연결을 해제하는 (clean-up)함수
 */
export const pickUi = (wrapperEl, keys, resData, delay = 200, onChange) => {
    const observers = [];
    if (!wrapperEl) return;

    // 청소배열들
    const intersectionObservers = [];
    const mutationObservers = [];

    const handleItemClick = (e) => {
        const item = e.target.closest(".picker-box__item");

        if (item) {
            item.scrollIntoView({
                behavior: "smooth", // 부드럽게 스크롤
                block: "center", // 스크롤 영역의 중앙에 위치
            });
        }
    };

    wrapperEl.addEventListener("click", handleItemClick);
    setTimeout(() => {
        // 스크롤이 일어나는 박스 찾기
        const columns = wrapperEl.querySelectorAll(".picker-box");

        columns.forEach((column, colIndex) => {
        // 초기값 세팅
        const currentKey = keys[colIndex];
        const targetValue = String(resData[currentKey]);

        // 스크롤을 감지하는ㄴ 옵저버
        const intersectionObs = new IntersectionObserver((entries) => {
            const activeEntry = entries.find(entry => entry.isIntersecting);
            if(activeEntry){
                const centerItem = activeEntry.target;
                const selectedValue = centerItem.textContent;

                // 클래스 꼬임 방지: 이 기둥 안의 모든 클래스를 일단 싹 다 초기화
                column.querySelectorAll(".picker-box__item").forEach((item) => {
                    item.classList.remove("is-selected", "is-near");
                });

                // 정중앙 아이템에 주인공 클래스 부여
                centerItem.classList.add("is-selected");

                // 바로 위, 바로 아래 아이템(형제 요소) 찾아서 is-near 부여!
                const prevItem = centerItem.previousElementSibling;
                const nextItem = centerItem.nextElementSibling;
                if (prevItem) prevItem.classList.add("is-near");
                if (nextItem) nextItem.classList.add("is-near");
            
                if(onChange) onChange(currentKey, selectedValue);
            }
        },{
            root: column, // 전체 화면 말고, 박스 안에서의 중앙 찾음
            rootMargin: "-40% 0px -40% 0px",
            threshold: 0.5,
        }
    );

        // 아이템에 옵저버 붙여주기
        const bindItems = () => {
            intersectionObs.disconnect();
            const items = column.querySelectorAll(".picker-box__item");
            items.forEach(item => intersectionObs.observe(item));
        }

        bindItems();
        const initialItem = Array.from(column.querySelectorAll(".picker-box__item")).find(item => {
              const itemValue = item.textContent.trim();
              const targetItem = targetValue.trim();
              if(!isNaN(itemValue) && !isNaN(targetItem)){
                return Number(itemValue) === Number(targetItem)
              }
              
              return itemValue == targetItem
            }
        );
        if (initialItem) {
            initialItem.scrollIntoView({ block: "center", behavior: "auto" });
        }

        intersectionObservers.push(intersectionObs);

        const mutationObs = new MutationObserver(() =>{
            bindItems();
        });
        
        mutationObs.observe(column, {childList:true, subtree:true});
        mutationObservers.push(mutationObs);

        });
    }, delay);

    // 컴포넌트가 사라질떄 실행할 함수를 반환
    return () => {
        intersectionObservers.forEach(obs => obs.disconnect());
        mutationObservers.forEach(obs => obs.disconnect());
        wrapperEl.removeEventListener("click", handleItemClick);
        
    };
};
